import { useCallback, useState, useRef } from 'react';
import { useRoute } from '@/context/RouteContext';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { AddressInput } from './AddressInput';
import { SummaryCard } from './SummaryCard';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Navigation, RotateCcw, Loader2, Sparkles, ChevronDown, FileDown, Printer, LocateFixed } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

// Sample Oklahoma test addresses - all unique
const TEST_ADDRESSES = {
  start: '100 N Broadway Ave, Oklahoma City, OK 73102',
  subject: '2501 NW 23rd St, Oklahoma City, OK 73107',
  addresses: [
    '3300 NW 63rd St, Oklahoma City, OK 73116',
    '5900 N May Ave, Oklahoma City, OK 73112',
    '7401 N Pennsylvania Ave, Oklahoma City, OK 73116',
    '2200 SW 59th St, Oklahoma City, OK 73119',
  ],
  finish: '5000 N Classen Blvd, Oklahoma City, OK 73118',
};

export const RoutePanel = ({ onCalculateRoute }) => {
  const {
    startAddress,
    setStartAddress,
    subjectProperty,
    setSubjectProperty,
    comparables,
    addComparable,
    removeComparable,
    updateComparable,
    finishAddress,
    setFinishAddress,
    routeResult,
    isCalculating,
    optimizedOrder,
    clearRoute,
    hasValidAddresses,
    setComparables,
  } = useRoute();

  const [isFillingTest, setIsFillingTest] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const placesLib = useMapsLibrary('places');

  // Geocode an address string to get location
  const geocodeAddress = useCallback(async (address) => {
    if (!placesLib) return null;
    
    const service = new placesLib.AutocompleteService();
    const placesService = new placesLib.PlacesService(document.createElement('div'));
    
    return new Promise((resolve) => {
      service.getPlacePredictions(
        { input: address, types: ['address'], componentRestrictions: { country: 'us' } },
        (predictions, status) => {
          if (status === 'OK' && predictions?.length > 0) {
            placesService.getDetails(
              { placeId: predictions[0].place_id, fields: ['place_id', 'formatted_address', 'geometry'] },
              (place, detailStatus) => {
                if (detailStatus === 'OK' && place) {
                  resolve({
                    address: place.formatted_address,
                    placeId: place.place_id,
                    location: {
                      lat: place.geometry.location.lat(),
                      lng: place.geometry.location.lng(),
                    },
                  });
                } else {
                  resolve(null);
                }
              }
            );
          } else {
            resolve(null);
          }
        }
      );
    });
  }, [placesLib]);

  // Use current location for start address
  const useCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    // Check permission state first if available
    if (navigator.permissions) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
        if (permissionStatus.state === 'denied') {
          toast.error('Location access is blocked. Please enable it in your browser settings.');
          return;
        }
      } catch (e) {
        // permissions API not fully supported, continue with geolocation request
      }
    }

    setIsGettingLocation(true);
    const loadingToast = toast.loading('Getting your location...');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // Reverse geocode to get address
        if (placesLib) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            { location: { lat: latitude, lng: longitude } },
            (results, status) => {
              setIsGettingLocation(false);
              toast.dismiss(loadingToast);
              if (status === 'OK' && results[0]) {
                setStartAddress({
                  address: results[0].formatted_address,
                  placeId: results[0].place_id,
                  location: { lat: latitude, lng: longitude },
                });
                toast.success('Location set!');
              } else {
                // Still set location even without address
                setStartAddress({
                  address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
                  placeId: '',
                  location: { lat: latitude, lng: longitude },
                });
                toast.success('Location set (coordinates)');
              }
            }
          );
        } else {
          setIsGettingLocation(false);
          toast.dismiss(loadingToast);
          setStartAddress({
            address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
            placeId: '',
            location: { lat: latitude, lng: longitude },
          });
          toast.success('Location set (coordinates)');
        }
      },
      (error) => {
        setIsGettingLocation(false);
        toast.dismiss(loadingToast);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Location access denied. Please allow location in your browser settings and try again.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Your location could not be determined. Please try again.');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out. Please try again.');
            break;
          default:
            toast.error('Unable to get location. Please try again.');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, [placesLib, setStartAddress]);

  // Fill all fields with test addresses
  const fillTestAddresses = useCallback(async () => {
    if (!placesLib) {
      toast.error('Maps not loaded yet');
      return;
    }

    setIsFillingTest(true);
    toast.info('Filling test addresses...');

    try {
      // Geocode all addresses in parallel
      const [startResult, subjectResult, finishResult, ...compResults] = await Promise.all([
        geocodeAddress(TEST_ADDRESSES.start),
        geocodeAddress(TEST_ADDRESSES.subject),
        geocodeAddress(TEST_ADDRESSES.finish),
        ...TEST_ADDRESSES.addresses.map(addr => geocodeAddress(addr)),
      ]);

      if (startResult) setStartAddress(startResult);
      if (subjectResult) setSubjectProperty(subjectResult);
      if (finishResult) setFinishAddress(finishResult);
      
      // Set comparables
      const validComps = compResults.filter(Boolean);
      if (validComps.length > 0) {
        setComparables(validComps.map((comp, idx) => ({
          id: idx + 1,
          ...comp,
        })));
      }

      toast.success('Test addresses loaded!');
    } catch (error) {
      console.error('Error filling test addresses:', error);
      toast.error('Failed to load test addresses');
    } finally {
      setIsFillingTest(false);
    }
  }, [placesLib, geocodeAddress, setStartAddress, setSubjectProperty, setFinishAddress, setComparables]);

  const handleCalculate = useCallback(async () => {
    if (!hasValidAddresses()) {
      toast.error('Please enter at least Start, Subject Property, and Finish addresses');
      return;
    }
    await onCalculateRoute();
  }, [hasValidAddresses, onCalculateRoute]);

  // Calculate totals
  const totalDuration = routeResult?.routes?.[0]?.legs?.reduce(
    (sum, leg) => sum + (leg.duration?.value || 0), 0
  );
  const totalDistance = routeResult?.routes?.[0]?.legs?.reduce(
    (sum, leg) => sum + (leg.distance?.value || 0), 0
  );

  // Format duration for display
  const formatDuration = (seconds) => {
    if (!seconds) return '--';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} min`;
  };

  // Format distance for display
  const formatDistance = (meters) => {
    if (!meters) return '--';
    const miles = meters * 0.000621371;
    return `${miles.toFixed(1)} mi`;
  };

  // Generate PDF
  const handleExportPDF = useCallback(() => {
    if (!routeResult) {
      toast.error('Please calculate a route first');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;

      // Title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('RouteScout - Appraisal Route', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      // Date
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Summary Box
      doc.setFillColor(240, 240, 240);
      doc.rect(14, yPos, pageWidth - 28, 25, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total Time: ${formatDuration(totalDuration)}`, 20, yPos + 10);
      doc.text(`Total Distance: ${formatDistance(totalDistance)}`, 20, yPos + 18);
      yPos += 35;

      // Start Address
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(34, 197, 94); // Green
      doc.text('START:', 14, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(startAddress.address || 'Not set', 35, yPos);
      yPos += 10;

      // Optimized Visit Order
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Optimized Visit Order:', 14, yPos);
      yPos += 8;

      optimizedOrder.forEach((stop, index) => {
        doc.setFontSize(11);
        if (stop.type === 'subject') {
          doc.setTextColor(217, 70, 239); // Purple
          doc.setFont('helvetica', 'bold');
          doc.text(`SP - Subject Property`, 14, yPos);
        } else {
          doc.setTextColor(245, 158, 11); // Amber
          doc.setFont('helvetica', 'bold');
          doc.text(`${stop.addressNum} - ${stop.label}`, 14, yPos);
        }
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(stop.address || '', 20, yPos + 5);
        yPos += 14;
      });

      // Finish Address
      yPos += 5;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 59, 48); // Red
      doc.text('FINISH:', 14, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(finishAddress.address || 'Not set', 38, yPos);

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('Generated by RouteScout', pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });

      // Save the PDF
      doc.save(`RouteScout_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  }, [routeResult, optimizedOrder, startAddress, finishAddress, totalDuration, totalDistance]);

  // Print function
  const handlePrint = useCallback(() => {
    if (!routeResult) {
      toast.error('Please calculate a route first');
      return;
    }

    // Create printable content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>RouteScout - Appraisal Route</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          h1 { text-align: center; color: #333; margin-bottom: 5px; }
          .date { text-align: center; color: #666; font-size: 12px; margin-bottom: 20px; }
          .summary { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .summary-row { display: flex; justify-content: space-between; }
          .summary-item { font-size: 18px; }
          .summary-label { color: #666; font-size: 12px; }
          .section-title { font-size: 16px; font-weight: bold; margin: 20px 0 10px; border-bottom: 2px solid #333; padding-bottom: 5px; }
          .address-row { margin: 10px 0; padding: 10px; border-left: 4px solid #ccc; }
          .address-row.start { border-color: #22C55E; }
          .address-row.subject { border-color: #D946EF; }
          .address-row.comparable { border-color: #F59E0B; }
          .address-row.finish { border-color: #FF3B30; }
          .address-label { font-weight: bold; color: #333; }
          .address-text { color: #666; margin-top: 3px; }
          .footer { text-align: center; color: #999; font-size: 10px; margin-top: 30px; padding-top: 10px; border-top: 1px solid #eee; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>RouteScout - Appraisal Route</h1>
        <div class="date">${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
        
        <div class="summary">
          <div class="summary-row">
            <div>
              <div class="summary-label">Total Time</div>
              <div class="summary-item">${formatDuration(totalDuration)}</div>
            </div>
            <div>
              <div class="summary-label">Total Distance</div>
              <div class="summary-item">${formatDistance(totalDistance)}</div>
            </div>
          </div>
        </div>

        <div class="address-row start">
          <div class="address-label">START</div>
          <div class="address-text">${startAddress.address || 'Not set'}</div>
        </div>

        <div class="section-title">Optimized Visit Order</div>
        ${optimizedOrder.map((stop, index) => `
          <div class="address-row ${stop.type === 'subject' ? 'subject' : 'address'}">
            <div class="address-label">${stop.type === 'subject' ? 'SP - Subject Property' : `${stop.addressNum} - ${stop.label}`}</div>
            <div class="address-text">${stop.address || ''}</div>
          </div>
        `).join('')}

        <div class="address-row finish">
          <div class="address-label">FINISH</div>
          <div class="address-text">${finishAddress.address || 'Not set'}</div>
        </div>

        <div class="footer">Generated by RouteScout</div>
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    } else {
      toast.error('Please allow popups to print');
    }
  }, [routeResult, optimizedOrder, startAddress, finishAddress]);

  return (
    <div className="h-full flex flex-col bg-zinc-50 dark:bg-zinc-950" data-testid="route-panel">
      {/* Header */}
      <div className="p-4 md:p-6 border-b border-border bg-white/90 dark:bg-black/90 backdrop-blur-md">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight text-foreground">
              RouteScout
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Oklahoma City Area Routes
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fillTestAddresses}
              disabled={isFillingTest}
              className="text-[#2563EB] border-[#2563EB]/30 hover:bg-[#2563EB]/10"
              data-testid="fill-test-btn"
            >
              {isFillingTest ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-1" />
              )}
              Test
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearRoute}
              className="text-muted-foreground hover:text-destructive"
              data-testid="clear-route-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <ScrollArea className="flex-1">
        <div className="p-4 md:p-6 pb-20 md:pb-6 space-y-6">
          {/* Address Inputs */}
          <Card className="p-4 space-y-4 bg-white dark:bg-zinc-900 border-border">
            {/* Collect all used addresses for deduplication */}
            {(() => {
              const usedAddresses = [
                startAddress.address,
                subjectProperty.address,
                ...comparables.map(c => c.address),
                finishAddress.address,
              ].filter(Boolean);
              
              return (
                <>
                  <div className="space-y-2">
                    <AddressInput
                      type="start"
                      value={startAddress}
                      onChange={setStartAddress}
                      placeholder="Enter starting location"
                      usedAddresses={usedAddresses}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={useCurrentLocation}
                      disabled={isGettingLocation}
                      className="w-full h-9 text-xs border-[#22C55E]/30 text-[#22C55E] hover:bg-[#22C55E]/10"
                      data-testid="use-location-btn"
                    >
                      {isGettingLocation ? (
                        <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                      ) : (
                        <LocateFixed className="w-3 h-3 mr-2" />
                      )}
                      Use Current Location
                    </Button>
                  </div>

                  <div className="border-t border-border pt-4">
                    <AddressInput
                      type="subject"
                      value={subjectProperty}
                      onChange={setSubjectProperty}
                      placeholder="Enter subject property address"
                      usedAddresses={usedAddresses}
                    />
                  </div>

                  <div className="border-t border-border pt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Additional Addresses
                      </span>
                      {comparables.length < 4 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={addComparable}
                          className="h-7 text-xs text-[#2563EB] hover:text-[#2563EB] hover:bg-[#2563EB]/10"
                          data-testid="add-address-btn"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add ({comparables.length}/4)
                        </Button>
                      )}
                    </div>
                    {comparables.map((comp, index) => (
                      <AddressInput
                        key={comp.id}
                        type="address"
                        index={index + 1}
                        value={comp}
                        onChange={(data) => updateComparable(comp.id, data)}
                        onRemove={() => removeComparable(comp.id)}
                        canRemove={comparables.length > 1}
                        placeholder={`Enter address ${index + 1}`}
                        usedAddresses={usedAddresses}
                      />
                    ))}
                  </div>

                  <div className="border-t border-border pt-4">
                    <AddressInput
                      type="finish"
                      value={finishAddress}
                      onChange={setFinishAddress}
                      placeholder="Enter finishing location"
                      usedAddresses={usedAddresses}
                    />
                  </div>
                </>
              );
            })()}
          </Card>

          {/* Calculate Button */}
          <Button
            onClick={handleCalculate}
            disabled={isCalculating || !hasValidAddresses()}
            className="w-full h-12 bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white font-semibold text-base shadow-lg"
            data-testid="calculate-route-btn"
          >
            {isCalculating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <Navigation className="w-5 h-5 mr-2" />
                Calculate Optimal Route
              </>
            )}
          </Button>

          {/* Results */}
          {routeResult && (
            <div className="space-y-4">
              {/* Bouncing arrow indicator - Green and more visible */}
              <div className="flex flex-col items-center py-3 bg-[#22C55E]/10 rounded-lg border border-[#22C55E]/30">
                <span className="text-sm font-semibold text-[#22C55E] mb-1">Results Below</span>
                <ChevronDown className="w-8 h-8 text-[#22C55E] animate-bounce" />
              </div>
              
              <SummaryCard duration={totalDuration} distance={totalDistance} />
              
              {optimizedOrder.length > 0 && (
                <Card className="p-4 bg-white dark:bg-zinc-900 border-border">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
                    Optimized Visit Order
                  </h3>
                  <ol className="space-y-3">
                    {optimizedOrder.map((stop, index) => {
                      // Match colors to map markers
                      const isSubject = stop.type === 'subject';
                      const bgColor = isSubject 
                        ? 'bg-white border-2 border-[#D946EF]'  // White with purple border for subject
                        : 'bg-[#F59E0B]'; // Amber for comparables
                      const textColor = isSubject ? 'text-black' : 'text-white';
                      
                      return (
                        <li 
                          key={index} 
                          className="flex items-start gap-3 text-sm"
                          data-testid={`optimized-stop-${index}`}
                        >
                          <span className={`w-7 h-7 rounded-full ${bgColor} ${textColor} text-xs font-bold flex items-center justify-center shrink-0 mt-0.5`}>
                            {isSubject ? 'SP' : stop.addressNum || (index + 1)}
                          </span>
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium text-foreground">{stop.label}</span>
                            {stop.address && (
                              <span className="text-xs text-muted-foreground">{stop.address}</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </Card>
              )}

              {/* Export/Print Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleExportPDF}
                  variant="outline"
                  className="flex-1 h-11 border-[#2563EB]/30 text-[#2563EB] hover:bg-[#2563EB]/10"
                  data-testid="export-pdf-btn"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Save PDF
                </Button>
                <Button
                  onClick={handlePrint}
                  variant="outline"
                  className="flex-1 h-11 border-[#2563EB]/30 text-[#2563EB] hover:bg-[#2563EB]/10"
                  data-testid="print-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
